/*
 * Dark mode for reporting pages (O-3959). A new reporting page gets dark mode
 * for free if it does two things:
 *
 *   1. Its Rails view opts in:  - content_for :body_class, 'od-themed'
 *      (the app layout sets <html data-theme> from the user's preference; the
 *       --sl-* dark values are gated under `html[data-theme="dark"] body.od-themed`).
 *
 *   2. Style with Setlist tokens, not raw hex: `var(--sl-text-primary, #101828)`,
 *      `var(--sl-bg-primary, #fff)`, `var(--sl-border-secondary, #e4e7ec)`, etc.
 *      The fallback keeps light mode unchanged; the token supplies the dark value.
 *      Token names/values: https://opendate-design-system.netlify.app/
 *
 * Leave hex LITERAL (do NOT tokenize) when the color must be the same in both
 * themes: chart/data-viz series colors, and text/bg on a fixed-color surface
 * (e.g. a status badge with a constant green/red fill, or labels on a black
 * tooltip). `var()` also does not resolve inside SVG presentation attributes
 * (`fill="#…"`, `stroke="#…"`) — only inside `style`, so leave those literal too.
 *
 * If a token you need has no dark value yet, add it to the dark blocks in
 * app/assets/stylesheets/application.scss (both [data-theme="dark"] and the
 * prefers-color-scheme mirror).
 */
import React from 'react';
import ReportingSidebar from './ReportingSidebar';
import DateRangePicker from './DateRangePicker';
import FilterSidebar from '../../shared/components/FilterSidebar';
import ActiveFilterBadges from '../../shared/components/ActiveFilterBadges';

function ReportingPageLayout({
  team,
  activeKey,
  title,
  subtitle,
  dateRange,
  setDateRange,
  isMobile,
  sidebarOpen,
  setSidebarOpen,
  filterSidebarOpen,
  setFilterSidebarOpen,
  activeFilters,
  filterGroups,
  handleFilterChange,
  handleRemoveFilter,
  handleClearAllFilters,
  filterTitle,
  filterSubtitle,
  toolbarExtras,
  headerActions,
  showDatePicker = true,
  showFilters = true,
  children,
}) {
  const hasFilters = Object.keys(activeFilters).length > 0;

  return (
    <div className="contacts-page">
      {isMobile && (
        <ReportingSidebar team={team} activeKey={activeKey} variant="mobile" />
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        {!isMobile && sidebarOpen && (
          <div style={{ width: 268, flexShrink: 0, marginTop: 4, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto' }}>
            <ReportingSidebar
              team={team}
              activeKey={activeKey}
              onToggleSidebar={() => setSidebarOpen(false)}
            />
          </div>
        )}

        <div className="contacts-main-column" style={{ flex: 1, minWidth: 0, marginTop: 32, paddingBottom: 100 }}>
          <div
            className={`d-flex align-items-start mb-3${headerActions ? ' flex-wrap' : ''}`}
            style={headerActions ? { rowGap: 8 } : undefined}
          >
            {!isMobile && !sidebarOpen && (
              <button
                type="button"
                className="btn btn-sm mr-3 d-inline-flex align-items-center justify-content-center"
                onClick={() => setSidebarOpen(true)}
                aria-label="Show sidebar"
                style={{
                  border: '1px solid var(--sl-border-primary, #d0d5dd)',
                  background: 'var(--sl-bg-primary, #fff)',
                  color: 'var(--sl-text-secondary, #A4A7AE)',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ transform: 'rotate(180deg)' }}>
                  <path d="M13.3332 11.3333H2.6665M2.6665 11.3333L5.33317 14M2.6665 11.3333L5.33317 8.66667M13.3332 4.66667H5.99984M5.99984 4.66667L8.6665 7.33333M5.99984 4.66667L8.6665 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="h4 mb-1" style={{ fontWeight: 700 }}>{title}</h2>
              <p className="text-muted mb-0" style={{ fontSize: 14 }}>{subtitle}</p>
            </div>
            {headerActions && (
              <div className="ml-auto d-flex flex-wrap" style={{ gap: 8 }}>{headerActions}</div>
            )}
          </div>

          <div
            className="d-flex align-items-center flex-wrap mb-4"
            style={{ gap: 8, padding: '10px 0' }}
          >
            {showDatePicker && (
              <>
                <DateRangePicker dateRange={dateRange} onChange={setDateRange} />
                <div style={{ width: 1, height: 20, background: 'var(--sl-border-primary, #d0d5dd)', flexShrink: 0 }} />
              </>
            )}

            {showFilters && <button
              type="button"
              className="btn btn-sm d-flex align-items-center"
              onClick={() => setFilterSidebarOpen(true)}
              style={{
                fontSize: 13,
                fontWeight: 500,
                padding: '4px 12px',
                borderRadius: 6,
                border: hasFilters ? '1px solid var(--sl-fg-brand, #1982c4)' : '1px solid var(--sl-border-primary, #d0d5dd)',
                background: hasFilters ? 'var(--sl-bg-brand-primary, #f6fafe)' : 'var(--sl-bg-primary, #fff)',
                color: hasFilters ? 'var(--sl-fg-brand, #1982c4)' : 'var(--sl-text-secondary, #344054)',
                height: 32,
              }}
            >
              <i className="fas fa-sliders-h mr-2" style={{ fontSize: 12 }} />
              Filters
              {hasFilters && (
                <span
                  className="ml-1"
                  style={{
                    background: 'var(--sl-fg-brand, #1982c4)',
                    color: 'var(--sl-text-white, #fff)',
                    borderRadius: 10,
                    padding: '0 6px',
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: '18px',
                  }}
                >
                  {Object.keys(activeFilters).length}
                </span>
              )}
            </button>}

            {toolbarExtras}
          </div>

          {hasFilters && (
            <div className="mb-3">
              <ActiveFilterBadges
                activeFilters={activeFilters}
                filterGroups={filterGroups}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
                onOpenFilterSidebar={() => setFilterSidebarOpen(true)}
              />
            </div>
          )}

          <FilterSidebar
            isOpen={filterSidebarOpen}
            onClose={() => setFilterSidebarOpen(false)}
            onApply={() => {}}
            title={filterTitle}
            subtitle={filterSubtitle}
            filterGroups={filterGroups}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
          />

          {children}
        </div>
      </div>
    </div>
  );
}

export default ReportingPageLayout;
